import { batch } from 'react-redux';
import { EmeraldError } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { LoyaltyProgram } from 'src/common/enumerations/customer-loyalty';
import { CustomerNotFound, WicTenderRestriction } from 'src/common/helpers/error-code';
import { ICustomer } from 'src/common/interfaces/dtos/ICustomer';
import { ItemListActions } from 'src/common/redux/reducers/item-list-reducer';
import { MiddleAreaActions } from 'src/common/redux/reducers/middle-area-reducer';
import { PaymentMessage } from 'src/devices/payment/dtos/eps-data';
import { applyCartResponse } from 'src/home-page/business-logic/common/apply-cart-response';
import {
  CartCustomerData,
  CartResponse,
} from 'src/items-list/business-logic/service/interfaces/cart-response';
import { showEwicErrorModal } from 'src/tendering/business-logic/handle-ewic-error-handling';

import {
  addCustomer,
  ISetTransactionCustomerParams,
} from '../../common/helpers/transaction-helper';
import { store } from '../../common/redux/configure-store';
import { TransactionActions } from '../../common/redux/reducers/transaction-reducer';
import { voidEmptyTransaction } from '../../home-page/business-logic/void-transaction';
import { initiateTransaction } from '../../items-list/business-logic/initiate-transaction';
import {
  handleCustomerLoyaltySearch,
  onCustomerNotFoundError,
} from './handle-customer-loyalty-search';
import { searchCustomersByCriteria } from './search-customers';

export const setLoyaltyCustomer = async (
  customer: ICustomer,
  entryMethod: string,
  searchAndAdd: boolean,
): Promise<void> => {
  const { transactionIdExists } = store.getState().transaction;
  if (!transactionIdExists) {
    await initiateTransaction();
  }
  const transactionId = store.getState().transaction.transactionId;

  const setCustomerParams: ISetTransactionCustomerParams = {
    customer,
    transactionId,
    entryMethod,
    searchAndAdd,
  };

  return withSpinner(async () => {
    await addCustomer(setCustomerParams)
      .then(dispatchCustomer)
      .catch(err => {
        if (err.code === CustomerNotFound) {
          return onCustomerNotFoundError(err);
        }
        if (err.code === WicTenderRestriction) {
          err.message = 'EwicAddRemoveLoyalty';
          return showEwicErrorModal(err);
        }
        if (!transactionIdExists) {
          voidEmptyTransaction();
        }
        throw err;
      });
  });
};

const dispatchCustomer = (cartResponse: CartResponse): void => {
  if (!cartResponse.customer) {
    throw new EmeraldError(CustomerNotFound);
  } else {
    const cust: CartCustomerData = cartResponse.customer;
    const programBalances = cust.programBalances;
    cust.points =
      programBalances?.length === 0
        ? undefined
        : programBalances
            ?.filter(program => program.programType !== LoyaltyProgram.StoredValue)
            .reduce((a, b) => a + (b.earnedPoints || 0), 0);
    applyCartResponse(cartResponse);
    batch((): void => {
      store.dispatch(TransactionActions.setCustomer(cust));
      store.dispatch(ItemListActions.selectItem(-1));
      store.dispatch(MiddleAreaActions.showActionWindow());
    });
  }
};

export const replaceCustomerInCart = async (): Promise<void> => {
  popEmeraldModal();
  handleCustomerLoyaltySearch();
};

export const extractLoyaltyID = (customer: ICustomer): string | undefined => {
  return customer.identifiers?.find(id => id.fieldName === 'loyaltyCard')?.fieldValue || undefined;
};

export const handleCustomerAddFromPinPad = async (result: PaymentMessage): Promise<void> => {
  if (result.promptDetails?.inputValue) {
    const searchCriteria = {
      alternateId: result.promptDetails.inputValue.replace(/\D/g, ''),
    };
    return searchCustomersByCriteria(searchCriteria, 0, 1)
      .then(async customer => {
        const params: ISetTransactionCustomerParams = {
          transactionId: store.getState().transaction.transactionId,
          customer: customer.pageContent[0],
          entryMethod: 'keyed',
          searchAndAdd: true,
        };
        return addCustomer(params);
      })
      .then(dispatchCustomer)
      .catch(() => {});
  }
};
